/**
 * Price Action V1.0 Strategy — faithful port of MT4 Price_Action.mq4
 *
 * Uses a single SuperTrend system (ATR-based):
 *   - Period = 8, Multiplier = 4.5 (configurable)
 *   - Signal on direction flip (crossover)
 *
 * SL/TP (Candle-range based from MT4):
 *   BUY:  range = High[i] - Low[i-1]
 *         TP1 = Entry + 1.5 × range, TP2 = Entry + 3.5 × range, SL = Entry - 1.8 × range
 *   SELL: range = High[i-1] - Low[i]
 *         TP1 = Entry - 1.5 × range, TP2 = Entry - 3.5 × range, SL = Entry + 1.8 × range
 *
 * Trailing Stop = SuperTrend band value (ratcheted)
 *
 * MT4 features ported:
 *   - Single SuperTrend with ATR
 *   - Buy/Sell arrow markers (arrows 233/234)
 *   - Custom colored candles: Green C'29,151,108', Red C'189,11,7', Gray
 *   - Candle-range SL/TP multipliers (1.5×, 3.5×, 1.8×)
 *   - SuperTrend-based trailing stop
 *   - Target/SL/TSL hit detection with markers
 *   - Live P/L, booked P/L, status tracking
 *   - Dashboard metadata for overlay component
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

// ─── Constants ───────────────────────────────────────────────────
const DIR_NONE = 0;
const DIR_LONG = 1;
const DIR_SHORT = -1;
type Direction = typeof DIR_NONE | typeof DIR_LONG | typeof DIR_SHORT;

const TP1_MULT = 1.5;
const TP2_MULT = 3.5;
const SL_MULT = 1.8;

// ─── ATR computation (Wilder smoothing) ──────────────────────────
function computeATR(candles: CandleData[], period: number): number[] {
  const atr: number[] = new Array(candles.length).fill(0);
  if (candles.length < 2) return atr;
  const tr: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high, l = candles[i].low, pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  let sum = 0;
  for (let i = 0; i < Math.min(period, tr.length); i++) sum += tr[i];
  atr[period - 1] = sum / period;
  for (let i = period; i < tr.length; i++) {
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  return atr;
}

// ─── Dashboard data interface ────────────────────────────────────
export interface PriceActionDashboard {
  signal: "BUY" | "SELL" | null;
  trend: "LONG" | "SHORT" | "NEUTRAL";
  entry: number | null;
  stopLoss: number | null;
  trailingStop: number | null;
  target1: number | null;
  target2: number | null;
  currentPrice: number;
  livePL: number;
  bookedPL: number;
  status: "Open" | "TGT-1 Hit" | "TGT-2 Hit" | "SL Hit" | "Trailing SL Hit";
  tp1Hit: boolean;
  tp2Hit: boolean;
  slHit: boolean;
  tslHit: boolean;
  candleState: "long" | "short" | "neutral";
}

export const priceActionIndicator: IndicatorDefinition = {
  id: "price_action",
  name: "Price Action",
  shortName: "PAC",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "period", label: "Period", type: "number", default: 8, hidden: true },
    { key: "multiplier", label: "Multiplier", type: "number", default: 4.5, hidden: true },
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [
      { label: "Yes", value: "yes" }, { label: "No", value: "no" },
    ]},
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "entryLineWidth", label: "Entry Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "tgtLineWidth", label: "Target Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "slLineWidth", label: "SL Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#1E90FF", style: "line", lineWidth: 1, lineStyle: 2, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#FF0000", style: "line", lineWidth: 1, lineStyle: 2, lastValueVisible: false },
    { key: "target1", label: "Target 1", color: "#006400", style: "line", lineWidth: 1, lineStyle: 2, lastValueVisible: false },
    { key: "target2", label: "Target 2", color: "#006400", style: "line", lineWidth: 1, lineStyle: 2, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const period = params.period ?? 8;
    const multiplier = params.multiplier ?? 4.5;
    const displayLevels = params.displayLevels ?? "yes";
    const showBackground = params.showBackground ?? "yes";
    const entryLW = Math.max(1, Math.min(4, params.entryLineWidth ?? 1));
    const tgtLW = Math.max(1, Math.min(4, params.tgtLineWidth ?? 1));
    const slLW = Math.max(1, Math.min(4, params.slLineWidth ?? 1));
    const len = candles.length;
    const minBars = period + 2;

    const emptyResult: PlotData[] = [
      { key: "entry", data: [] }, { key: "stopLoss", data: [] },
      { key: "target1", data: [] }, { key: "target2", data: [] },
      { key: "trailingStop", data: [] },
    ];

    if (len < minBars) return emptyResult;

    // ════════════════════════════════════════════════════════════
    // 1. Inline SuperTrend (MT4 PM_PERIOD=8, PM_MULTIPLIER=4.5)
    // ════════════════════════════════════════════════════════════
    const atr = computeATR(candles, period);
    const upper: number[] = new Array(len).fill(0);
    const lower: number[] = new Array(len).fill(0);
    const direction: Direction[] = new Array(len).fill(DIR_LONG);
    const longTS: number[] = new Array(len).fill(0);
    const shortTS: number[] = new Array(len).fill(0);

    for (let i = 0; i < len; i++) {
      const mid = (candles[i].high + candles[i].low) / 2;
      upper[i] = mid + multiplier * atr[i];
      lower[i] = mid - multiplier * atr[i];
    }

    for (let i = 1; i < len; i++) {
      const c = candles[i].close;
      if (c > upper[i - 1]) direction[i] = DIR_LONG;
      else if (c < lower[i - 1]) direction[i] = DIR_SHORT;
      else direction[i] = direction[i - 1];

      if (direction[i] === DIR_LONG && lower[i] < lower[i - 1]) lower[i] = lower[i - 1];
      if (direction[i] === DIR_SHORT && upper[i] > upper[i - 1]) upper[i] = upper[i - 1];

      const prev = direction[i - 1];
      if (direction[i] === DIR_SHORT && prev === DIR_LONG) upper[i] = (candles[i].high + candles[i].low) / 2 + multiplier * atr[i];
      if (direction[i] === DIR_LONG && prev === DIR_SHORT) lower[i] = (candles[i].high + candles[i].low) / 2 - multiplier * atr[i];

      if (direction[i] === DIR_LONG) longTS[i] = lower[i];
      else shortTS[i] = upper[i];
    }

    // ════════════════════════════════════════════════════════════
    // 2. Signal Generation — direction flip detection
    // ════════════════════════════════════════════════════════════
    const markers: IndicatorMarker[] = [];
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number }[] = [];
    const SIGNAL: Direction[] = new Array(len).fill(DIR_NONE);
    const ENTRY: number[] = new Array(len).fill(0);
    const SL: number[] = new Array(len).fill(0);
    const TSL: number[] = new Array(len).fill(0);
    const TP1: number[] = new Array(len).fill(0);
    const TP2: number[] = new Array(len).fill(0);
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");

    let lastSignalDir: Direction = DIR_NONE;
    let lastSignalBar = -1;
    const lastClosedBar = len - 2;

    for (let i = 1; i <= lastClosedBar; i++) {
      const curDir = direction[i];
      const prevDir = direction[i - 1];
      const isBuySignal = curDir === DIR_LONG && prevDir === DIR_SHORT;
      const isSellSignal = curDir === DIR_SHORT && prevDir === DIR_LONG;

      if (isBuySignal && i > 0) {
        SIGNAL[i] = DIR_LONG;
        ENTRY[i] = candles[i].close;
        const range = Math.max(candles[i].high - (i > 0 ? candles[i - 1].low : candles[i].low), 0.01);
        TP1[i] = ENTRY[i] + TP1_MULT * range;
        TP2[i] = ENTRY[i] + TP2_MULT * range;
        SL[i] = ENTRY[i] - SL_MULT * range;
        TSL[i] = SL[i]; // Initially same as SL
        lastSignalDir = DIR_LONG;
        lastSignalBar = i;

        markers.push({
          time: candles[i].time, position: "belowBar",
          color: "#1d976c", shape: "arrowUp", text: "", size: 2,
        });
        arrowMarkers.push({ time: candles[i].time, direction: "long" as const, price: candles[i].low });
      } else if (isSellSignal && i > 0) {
        SIGNAL[i] = DIR_SHORT;
        ENTRY[i] = candles[i].close;
        const range = Math.max((i > 0 ? candles[i - 1].high : candles[i].high) - candles[i].low, 0.01);
        TP1[i] = ENTRY[i] - TP1_MULT * range;
        TP2[i] = ENTRY[i] - TP2_MULT * range;
        SL[i] = ENTRY[i] + SL_MULT * range;
        TSL[i] = SL[i];
        lastSignalDir = DIR_SHORT;
        lastSignalBar = i;

        markers.push({
          time: candles[i].time, position: "aboveBar",
          color: "#BD0B07", shape: "arrowDown", text: "", size: 2,
        });
        arrowMarkers.push({ time: candles[i].time, direction: "short" as const, price: candles[i].high });
      } else {
        SIGNAL[i] = lastSignalDir;
        ENTRY[i] = ENTRY[i - 1] || 0;
        SL[i] = SL[i - 1] || 0;
        TP1[i] = TP1[i - 1] || 0;
        TP2[i] = TP2[i - 1] || 0;
      }

      // Trailing stop tracks SuperTrend band, replacing SL when tighter
      if (lastSignalDir === DIR_LONG && longTS[i] > 0) {
        TSL[i] = longTS[i];
      } else if (lastSignalDir === DIR_SHORT && shortTS[i] > 0) {
        TSL[i] = shortTS[i];
      } else {
        TSL[i] = TSL[i - 1] || 0;
      }

      // Candle color state
      if (lastSignalDir === DIR_LONG) candleColorState[i] = "long";
      else if (lastSignalDir === DIR_SHORT) candleColorState[i] = "short";
      else candleColorState[i] = "neutral";
    }

    // Forward-fill to forming bar
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      for (let i = lastClosedBar + 1; i < len; i++) {
        SIGNAL[i] = SIGNAL[lastClosedBar]; ENTRY[i] = ENTRY[lastClosedBar];
        SL[i] = SL[lastClosedBar]; TSL[i] = TSL[lastClosedBar];
        TP1[i] = TP1[lastClosedBar]; TP2[i] = TP2[lastClosedBar];
        candleColorState[i] = candleColorState[lastClosedBar];
      }
    }

    // ════════════════════════════════════════════════════════════
    // 3. Target / SL / TSL hit detection
    // ════════════════════════════════════════════════════════════
    let status: PriceActionDashboard["status"] = "Open";
    let tp1Hit = false, tp2Hit = false, slHit = false, tslHit = false;
    let bookedPL = 0;
    let isClosed = false;

    if (lastSignalBar >= 0) {
      const entryVal = ENTRY[lastSignalBar];
      const slVal = SL[lastSignalBar];
      const tp1Val = TP1[lastSignalBar];
      const tp2Val = TP2[lastSignalBar];

      for (let i = lastSignalBar + 1; i <= Math.min(lastClosedBar, len - 1); i++) {
        if (isClosed) break;

        if (SIGNAL[lastSignalBar] === DIR_LONG) {
          // SL check
          if (!slHit && candles[i].low <= slVal) {
            slHit = true; status = "SL Hit"; bookedPL = slVal - entryVal; isClosed = true;
            continue;
          }
          // TSL check  
          const tslVal = TSL[i];
          if (!tslHit && tslVal > slVal && candles[i].low <= tslVal) {
            tslHit = true; status = "Trailing SL Hit"; bookedPL = tslVal - entryVal; isClosed = true;
            continue;
          }
          // TGT-2 check (closes trade)
          if (!tp2Hit && candles[i].high >= tp2Val) {
            tp2Hit = true; tp1Hit = true; status = "TGT-2 Hit"; bookedPL = tp2Val - entryVal; isClosed = true;
            continue;
          }
          // TGT-1 check (partial, doesn't close)
          if (!tp1Hit && candles[i].high >= tp1Val) {
            tp1Hit = true; status = "TGT-1 Hit"; bookedPL = tp1Val - entryVal;
          }
        } else if (SIGNAL[lastSignalBar] === DIR_SHORT) {
          if (!slHit && candles[i].high >= slVal) {
            slHit = true; status = "SL Hit"; bookedPL = entryVal - slVal; isClosed = true;
            continue;
          }
          const tslVal = TSL[i];
          if (!tslHit && tslVal < slVal && candles[i].high >= tslVal) {
            tslHit = true; status = "Trailing SL Hit"; bookedPL = entryVal - tslVal; isClosed = true;
            continue;
          }
          if (!tp2Hit && candles[i].low <= tp2Val) {
            tp2Hit = true; tp1Hit = true; status = "TGT-2 Hit"; bookedPL = entryVal - tp2Val; isClosed = true;
            continue;
          }
          if (!tp1Hit && candles[i].low <= tp1Val) {
            tp1Hit = true; status = "TGT-1 Hit"; bookedPL = entryVal - tp1Val;
          }
        }
      }
    }

    // ════════════════════════════════════════════════════════════
    // 4. Dashboard metadata
    // ════════════════════════════════════════════════════════════
    const uiBar = Math.max(0, len - 2);
    const currentPrice = candles[len - 1]?.close ?? 0;
    const currSignal = SIGNAL[uiBar];
    const currEntry = ENTRY[uiBar];
    const currSL = SL[uiBar];
    const currTSL = TSL[uiBar];
    const currTP1 = TP1[uiBar];
    const currTP2 = TP2[uiBar];

    let dashSignal: PriceActionDashboard["signal"] = null;
    let dashTrend: PriceActionDashboard["trend"] = "NEUTRAL";
    let livePL = 0;

    if (currSignal !== DIR_NONE && currEntry > 0) {
      if (currSignal === DIR_LONG) {
        dashSignal = "BUY"; dashTrend = "LONG"; livePL = currentPrice - currEntry;
      } else {
        dashSignal = "SELL"; dashTrend = "SHORT"; livePL = currEntry - currentPrice;
      }
    }

    // Effective SL = tighter of SL and TSL
    let effectiveSL = currSL;
    if (currTSL > 0 && currSignal === DIR_LONG && currTSL > currSL) effectiveSL = currTSL;
    if (currTSL > 0 && currSignal === DIR_SHORT && currTSL < currSL) effectiveSL = currTSL;

    const dashboard: PriceActionDashboard = {
      signal: dashSignal, trend: dashTrend,
      entry: currEntry > 0 ? currEntry : null,
      stopLoss: currSL > 0 ? currSL : null,
      trailingStop: currTSL > 0 ? currTSL : null,
      target1: currTP1 > 0 ? currTP1 : null,
      target2: currTP2 > 0 ? currTP2 : null,
      currentPrice, livePL, bookedPL, status,
      tp1Hit, tp2Hit, slHit, tslHit,
      candleState: candleColorState[uiBar] || "neutral",
    };

    // ════════════════════════════════════════════════════════════
    // 5. Build plot data + candle colors + hit markers
    // ════════════════════════════════════════════════════════════
    const entryData: { time: number; value: number }[] = [];
    const slData: { time: number; value: number }[] = [];
    const t1Data: { time: number; value: number }[] = [];
    const t2Data: { time: number; value: number }[] = [];

    // Data arrays intentionally left empty — priceLines handle all level drawing.

    // MT4 candle colors: C'29,151,108' for long, C'189,11,7' for short, Gray neutral
    const candleColors: { time: number; color: string }[] = [];
    if (showBackground !== "no") for (let i = 0; i < len; i++) {
      const state = candleColorState[i];
      if (state === "long") candleColors.push({ time: candles[i].time, color: "#1d976c" });
      else if (state === "short") candleColors.push({ time: candles[i].time, color: "#BD0B07" });
      else candleColors.push({ time: candles[i].time, color: "#808080" });
    }

    // Hit markers
    if (lastSignalBar >= 0 && (tp1Hit || tp2Hit || slHit || tslHit)) {
      const sigDir = SIGNAL[lastSignalBar];
      const tp1V = TP1[lastSignalBar], tp2V = TP2[lastSignalBar], slV = SL[lastSignalBar];
      let _tp1 = tp1Hit, _tp2 = tp2Hit, _sl = slHit, _tsl = tslHit;
      let closed = false;
      for (let i = lastSignalBar + 1; i <= Math.min(lastClosedBar, len - 1); i++) {
        if (closed) break;
        if (sigDir === DIR_LONG) {
          if (_sl && candles[i].low <= slV) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#FF0000", text: "✗ SL", size: 1 }); _sl = false; closed = true; continue; }
          if (_tsl && TSL[i] > slV && candles[i].low <= TSL[i]) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#FFA500", text: "✗ TSL", size: 1 }); _tsl = false; closed = true; continue; }
          if (_tp2 && candles[i].high >= tp2V) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#006400", text: "✓ T2", size: 1 }); _tp2 = false; closed = true; continue; }
          if (_tp1 && candles[i].high >= tp1V) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#006400", text: "✓ T1", size: 1 }); _tp1 = false; }
        } else {
          if (_sl && candles[i].high >= slV) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#FF0000", text: "✗ SL", size: 1 }); _sl = false; closed = true; continue; }
          if (_tsl && TSL[i] < slV && candles[i].high >= TSL[i]) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#FFA500", text: "✗ TSL", size: 1 }); _tsl = false; closed = true; continue; }
          if (_tp2 && candles[i].low <= tp2V) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#006400", text: "✓ T2", size: 1 }); _tp2 = false; closed = true; continue; }
          if (_tp1 && candles[i].low <= tp1V) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#006400", text: "✓ T1", size: 1 }); _tp1 = false; }
        }
      }
    }

    // ════════════════════════════════════════════════════════════
    // 6. Price lines — extend from entry candle to right edge + axis badges
    // ════════════════════════════════════════════════════════════
    const priceLines: IndicatorPriceLine[] = [];
    if (displayLevels !== "no" && lastSignalBar >= 0 && ENTRY[lastSignalBar] > 0) {
      const fromTime = candles[lastSignalBar].time;
      priceLines.push(
        { id: "pa-entry", price: ENTRY[lastSignalBar], color: "#1E90FF", title: "Entry", lineVisible: true, lineWidth: entryLW, lineStyle: 2, axisLabelVisible: true, fromTime },
        { id: "pa-tp1", price: TP1[lastSignalBar], color: "#006400", title: "TGT-1", lineVisible: true, lineWidth: tgtLW, lineStyle: 2, axisLabelVisible: true, fromTime },
        { id: "pa-tp2", price: TP2[lastSignalBar], color: "#006400", title: "TGT-2", lineVisible: true, lineWidth: tgtLW, lineStyle: 2, axisLabelVisible: true, fromTime },
        { id: "pa-sl", price: SL[lastSignalBar], color: "#FF0000", title: "Stop Loss", lineVisible: true, lineWidth: slLW, lineStyle: 0, axisLabelVisible: true, fromTime },
      );
    }

    return [
      { key: "entry", data: entryData, markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "stopLoss", data: slData },
      { key: "target1", data: t1Data },
      { key: "target2", data: t2Data },
    ];
  },
};
